/**
 * M27 Step 9 — Go Live + Reassurance (PR 7).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  LayoutDashboard,
  Sparkles,
  Target,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api, ApiError } from "../../lib/api";
import {
  ONBOARDING_JOURNEY_QUERY_KEY,
  STEP_SLUGS,
  type OnboardingState,
  type StepSlug,
} from "../../lib/onboarding";
import { useOnboardingAutoSave } from "../../lib/use-onboarding-autosave";
import { StepLayout } from "./StepLayout";

const _STEP_LABEL_KEYS: Record<StepSlug, string> = {
  welcome: "app.onb_step_welcome",
  company: "app.onb_step_company",
  policies: "app.onb_step_policies",
  "jobs-grades": "app.onb_step_jobs_grades",
  employees: "app.onb_step_employees",
  approvals: "app.onb_step_approvals",
  integrations: "app.onb_step_integrations",
  "shadow-payroll": "app.onb_step_shadow_payroll",
  "go-live": "app.onb_step_go_live",
};

export function Step9GoLiveReal() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const auto = useOnboardingAutoSave("go-live");
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const stateQ = useQuery({
    queryKey: ONBOARDING_JOURNEY_QUERY_KEY,
    queryFn: () => api.getOnboardingJourneyState(),
  });
  const state = stateQ.data as OnboardingState | undefined;

  const goLive = useMutation({
    mutationFn: async () => {
      await auto.markStatus("completed");
      const next = await api.updateOnboardingJourneyState({
        go_live_at: new Date().toISOString(),
      });
      qc.setQueryData(ONBOARDING_JOURNEY_QUERY_KEY, next);
      return next;
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? e.detail : String(e)),
  });

  const live = !!state?.go_live_at;
  const skippedSteps = STEP_SLUGS.filter(
    (s) => state?.steps?.[s]?.status === "skipped",
  );
  const completedSteps = STEP_SLUGS.filter(
    (s) =>
      s !== "go-live" &&
      state?.steps?.[s]?.status === "completed",
  );

  return (
    <StepLayout
      slug="go-live"
      title={t("app.onb_step_go_live")}
      subtitle={t("app.s9_subtitle")}
      hideContinue={live}
      continueLabel={t("app.s9_mark_complete")}
      onContinue={async () => {
        await Promise.resolve();
      }}
    >
      <div className="flex flex-col gap-5">
        {!live ? (
          <>
            <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
              <header className="flex items-center gap-2">
                <Target size={16} className="text-brand-blue" aria-hidden />
                <h2 className="text-sm font-semibold text-text-primary">
                  {t("app.s9_what_happens")}
                </h2>
              </header>
              <ul className="mt-3 flex flex-col gap-2 text-sm text-text-secondary">
                {[t("app.s9_what_1"), t("app.s9_what_2"), t("app.s9_what_3"), t("app.s9_what_4")].map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <CheckCircle2
                      size={14}
                      aria-hidden
                      className="mt-0.5 text-emerald-600"
                    />
                    {line}
                  </li>
                ))}
              </ul>
            </section>

            <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <SummaryCard
                tone="emerald"
                label={t("app.s9_steps_completed")}
                count={completedSteps.length}
                items={completedSteps.map((s) => t(_STEP_LABEL_KEYS[s]))}
              />
              <SummaryCard
                tone={skippedSteps.length > 0 ? "amber" : "neutral"}
                label={t("app.s9_steps_skipped")}
                count={skippedSteps.length}
                items={skippedSteps.map((s) => t(_STEP_LABEL_KEYS[s]))}
                hint={
                  skippedSteps.length > 0
                    ? t("app.s9_skipped_hint")
                    : t("app.s9_all_complete")
                }
              />
            </section>

            {err && (
              <p className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {err}
              </p>
            )}
            {!confirming ? (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="self-start rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:opacity-90"
              >
                {t("app.s9_ready_btn")}
              </button>
            ) : (
              <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <header className="flex items-start gap-2">
                  <AlertTriangle
                    size={16}
                    className="mt-0.5 text-amber-700"
                    aria-hidden
                  />
                  <div className="flex-1">
                    <h2 className="text-sm font-semibold text-amber-900">
                      {t("app.s9_are_you_ready")}
                    </h2>
                    <p className="mt-1 text-xs text-amber-800">
                      {t("app.s9_are_you_ready_body")}
                    </p>
                  </div>
                </header>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="rounded border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-default"
                  >
                    {t("app.s9_not_yet")}
                  </button>
                  <button
                    type="button"
                    onClick={() => goLive.mutate()}
                    disabled={goLive.isPending}
                    className="ms-auto rounded bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
                  >
                    {goLive.isPending ? t("app.s9_going_live") : t("app.s9_yes_golive")}
                  </button>
                </div>
              </section>
            )}
          </>
        ) : (
          <ReassuranceCard
            firstRunDate={
              state?.go_live_at
                ? new Date(state.go_live_at).toLocaleDateString()
                : ""
            }
          />
        )}
      </div>
    </StepLayout>
  );
}

export function ReassuranceCard({
  firstRunDate,
}: {
  firstRunDate?: string;
}) {
  const { t } = useTranslation();
  return (
    <article
      className="relative overflow-hidden rounded-2xl border border-ai-subtle px-6 py-8 sm:px-10 sm:py-12"
      style={{
        background:
          "radial-gradient(circle at 0% 0%, rgba(0, 211, 194, 0.10), transparent 50%), radial-gradient(circle at 100% 100%, rgba(168, 70, 191, 0.10), transparent 50%), var(--surface-default)",
      }}
    >
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-0.5 bg-brand-gradient-h"
      />
      <header className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ai-bg text-ai">
          <Sparkles size={14} aria-hidden />
        </span>
        <p className="text-xs font-semibold uppercase tracking-wider text-brand-blue">
          {t("app.s9_re_eyebrow")}
        </p>
      </header>
      <h1 className="mt-3 font-en-display text-2xl font-bold leading-tight text-brand-deep sm:text-3xl">
        {t("app.s9_re_headline")}
      </h1>
      <p className="mt-4 max-w-2xl text-base text-text-secondary">
        {t("app.s9_re_body_1")}{" "}
        <Link to="/settings" className="text-brand hover:underline">
          {t("app.s9_re_link_settings")}
        </Link>
        {t("app.s9_re_body_2")}
        <Link to="/policies" className="text-brand hover:underline">
          {t("app.s9_re_link_policies")}
        </Link>
        {t("app.s9_re_body_3")}{" "}
        <Link to="/ai/behavior" className="text-brand hover:underline">
          {t("app.s9_re_link_ai_config")}
        </Link>
        {t("app.s9_re_body_4")}{" "}
        <Link to="/onboarding" className="text-brand hover:underline">
          {t("app.s9_re_link_onboarding")}
        </Link>{" "}
        {t("app.s9_re_body_5")}
      </p>
      {firstRunDate && (
        <p className="mt-3 text-sm text-text-tertiary">
          {t("app.s9_activated_on", { date: firstRunDate })}
        </p>
      )}
      <nav className="mt-6 flex flex-wrap gap-2">
        <ReassuranceLink
          to="/mission-control"
          icon={LayoutDashboard}
          label={t("app.s9_open_mc")}
        />
        <ReassuranceLink
          to="/settings/audit-log"
          icon={Eye}
          label={t("app.s9_view_audit")}
        />
        <ReassuranceLink
          to="/ai/behavior"
          icon={ClipboardCheck}
          label={t("app.s9_change_ai_behavior")}
        />
      </nav>
    </article>
  );
}

function ReassuranceLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
    >
      <Icon size={14} aria-hidden />
      {label}
    </Link>
  );
}

function SummaryCard({
  tone,
  label,
  count,
  items,
  hint,
}: {
  tone: "emerald" | "amber" | "neutral";
  label: string;
  count: number;
  items: string[];
  hint?: string;
}) {
  const styles = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    neutral: "border-border-subtle bg-surface-default text-text-primary",
  }[tone];
  return (
    <article className={`rounded-xl border p-4 ${styles}`}>
      <header className="flex items-center gap-2">
        <h2 className="text-sm font-semibold">{label}</h2>
        <span className="ms-auto rounded-full bg-surface-default px-2 py-0.5 text-xs font-medium text-text-primary">
          {count}
        </span>
      </header>
      {items.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1 text-xs">
          {items.map((it) => (
            <li
              key={it}
              className="rounded-full bg-surface-default px-2 py-0.5 text-text-secondary"
            >
              {it}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs">{hint ?? "—"}</p>
      )}
      {items.length > 0 && hint && (
        <p className="mt-2 text-xs opacity-75">{hint}</p>
      )}
    </article>
  );
}

export type { StepSlug };
